import { useSceneStore } from '@/stores/scene-store';
import { encodeFieldKey } from '@/schemas/validation/ValidationSchemaGenerator';
import { SceneDataElement } from '@/schemas/sceneDataSchema';
import { ValidationType, DataType } from '@/constants/validationTypes';

interface EntityTarget {
  scope: string;
  entity_index: number;
}

/**
 * Optimistic validity for entity-level indicators (validity icons, in-progress chips).
 *
 * - Pending non-empty value: optimistically valid (user has staged a change).
 * - No pending change, non-null value: valid unless server explicitly rejected it (covers prefill data).
 * - Null boolean field: optimistically valid (checkbox auto-defaults to false on mount).
 * - Otherwise: trust the server's `valid` flag.
 */
export function isFieldOptimisticallyValid(
  field: SceneDataElement,
  pendingValue: SceneDataElement['value'] | undefined
): boolean {
  if (pendingValue !== undefined) {
    return pendingValue !== null && pendingValue !== '';
  }
  // If the field already has a non-null, non-empty value (e.g. from prefill),
  // treat it as valid unless the server has explicitly rejected it.
  if (field.value !== null && field.value !== '') {
    return field.valid !== false;
  }
  // Boolean/checkbox fields auto-default to `false` when their component mounts.
  // Treat null-valued booleans as optimistically valid so entity indicators don't
  // show "in progress" just because the form hasn't been expanded yet.
  if (
    field.value === null &&
    field.validation_rules.some((r) => r.type === ValidationType.DataType && r.value === DataType.Boolean)
  ) {
    return true;
  }
  // Empty field with no pending change — only invalid if the server explicitly
  // reported it as invalid (valid === false).
  return field.valid !== false;
}

/**
 * Common validation logic for checking if all fields in a collection are optimistically valid.
 */
function validateFields(
  fields: SceneDataElement[],
  state: {
    serverState: Record<string, SceneDataElement>;
    pendingChanges: Record<string, SceneDataElement['value']>;
  }
): boolean {
  return fields.every((field) => {
    const key = encodeFieldKey(field);
    const pendingValue = key in state.pendingChanges ? state.pendingChanges[key] : undefined;
    return isFieldOptimisticallyValid(field, pendingValue);
  });
}

// Returns true when every field for a single entity is valid.
export function useEntityValidity(scope: string, entityIndex: number): boolean {
  return useSceneStore((state) => {
    const entityFields = Object.values(state.serverState).filter(
      (field) => field.scope === scope && field.entity_index === entityIndex
    );

    if (entityFields.length === 0) return false;

    return validateFields(entityFields, state);
  });
}

/**
 * Returns true when every field in the scene store matching any of the given
 * scopes is optimistically valid. Returns `null` when no fields are found for
 * any scope (the step hasn't been visited yet).
 */
export function useScopesValidity(scopes: string[]): boolean | null {
  return useSceneStore((state) => {
    if (scopes.length === 0) return null;

    const scopeSet = new Set(scopes);
    const fields = Object.values(state.serverState).filter((field) => scopeSet.has(field.scope));

    if (fields.length === 0) return null;

    return validateFields(fields, state);
  });
}

/**
 * Returns true when every field for all specified entity targets is optimistically valid.
 * Each entity target consists of a scope and entity_index pair.
 * Returns false if no entity targets are provided or if any field in any target is invalid.
 */
export function useEntityTargetsValidity(entityTargets: EntityTarget[]): boolean {
  return useSceneStore((state) => {
    // If no entity targets, return false
    if (entityTargets.length === 0) return false;

    // Check if ALL entity targets are valid
    return entityTargets.every((target) => {
      const entityFields = Object.values(state.serverState).filter(
        (field) => field.scope === target.scope && field.entity_index === target.entity_index
      );

      // If no fields found for this entity target, consider it invalid
      if (entityFields.length === 0) return false;

      // Check if all fields for this entity target are valid
      return validateFields(entityFields, state);
    });
  });
}
